import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import { message, notification } from "antd";
import { useInvalidateAccountSummary } from "hooks/useAccountSummary";
import { useAuthCookiePersistence } from "hooks/useAuthCookiePersistence";
import { useInvalidateCustomerDashboard } from "hooks/useCustomerDashboard";
import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

export interface SignalR {
    connect: () => Promise<void>;
    disconnect: () => Promise<void>;
}

const SignalRContext = createContext<SignalR | null>(null);

/***
 * The connection hub on development environments connected to staging is the same as production, so just be careful of what accounts are being tested with.
 */

export const SignalRProvider: FC<
    PropsWithChildren & {
        enabled: boolean;
    }
> = ({ children, enabled }) => {
    const { authTokenFactory, isImpersonating } = useAuthCookiePersistence();
    const [connection, setConnection] = useState<HubConnection>();
    const invalidateUserSummary = useInvalidateAccountSummary();
    const invalidateDashboardData = useInvalidateCustomerDashboard();

    const createHubConnection = useCallback(async () => {
        if (!enabled || isImpersonating()) {
            return;
        }

        const conn = new HubConnectionBuilder()
            .withUrl(
                `${
                    process.env["REACT_APP_USE_STAGING"] === "true"
                        ? "https://staging.dcsports87.com"
                        : ""
                }/ws/devices`,
                {
                    accessTokenFactory: authTokenFactory,
                },
            )
            .configureLogging(LogLevel.None)
            .withAutomaticReconnect()
            .build();

        conn.on("emit", (...args: object[]) => {
            notification.info({
                message: "Debug Event",
                description: JSON.stringify(args, null, 2),
                placement: "top",
                maxCount: 1,
                duration: 2,
            });
        });

        conn.on("consignmentBalanceUpdated", () => {
            message.info("Your balance has been updated!");
            invalidateUserSummary();
        });

        conn.on("submissionReceived", (friendlyPackageId: number) => {
            console.log("here");
            message.info(`We received your package! (#${friendlyPackageId})`);
            invalidateDashboardData();
        });

        conn.on("supportTicketReplied", (ticketId: number) => {
            console.log("here");
            // TODO: Add Link here
            message.info(`Your ticket #${ticketId} has been replied to!, `);
        });

        setConnection(conn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authTokenFactory, enabled]);

    const connect = useCallback(async () => {
        try {
            if (connection?.state === HubConnectionState.Disconnected) {
                await connection.start();
            }
        } catch (err) {
            console.error(err);
        }
    }, [connection]);

    const disconnect = useCallback(async () => {
        if (connection?.state === HubConnectionState.Connected) {
            await connection.stop();
        }
    }, [connection]);

    useEffect(() => {
        createHubConnection();
    }, [createHubConnection]);

    return (
        <SignalRContext.Provider
            value={{
                connect,
                disconnect,
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
};

export function useSignalR() {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error("useSignalR must be used within an SignalRProvider");
    }
    return context;
}
